import { useState, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { sanitizeMarkdown } from '@/features/chat/shared';
import {
  askMultiFileQuestionStream,
  askSingleFileQuestionStream,
  askGroqWithToolsStream,
  ToolCallEvent,
} from '@/services/multiFileAskAIService';

export interface StreamingChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface ToolCallStep {
  id: string;
  type: 'search' | 'analyze' | 'process' | 'tool';
  toolName?: string;
  description: string;
  status: 'pending' | 'running' | 'completed';
  args?: Record<string, unknown>;
  result?: unknown;
}

export interface StreamingChatOptions {
  mode: 'single' | 'multi' | 'groq';
  teamId: string;
  fileId?: string; // Required for single mode
  fileIds?: string[]; // Optional for multi/groq mode - will auto-search if not provided
  sessionId?: string | null;
  mediaAnalysisOptions?: any;
  searchLimit?: number; // Number of files to auto-select when searching (default: 5)
  enabledTools?: string[]; // For groq mode - tools the LLM can use
  model?: string; // For groq mode - model to use (default: kimi-k2)
}

export interface UseStreamingChatParams {
  onMessageAdd?: (message: StreamingChatMessage) => void;
  onMessageUpdate?: (content: string) => void;
  onSessionUpdate?: (sessionId: string) => void;
  onFilesSelected?: (fileIds: string[]) => void;
  onToolCallUpdate?: (steps: ToolCallStep[]) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export const useStreamingChat = ({
  onMessageAdd,
  onMessageUpdate,
  onSessionUpdate,
  onFilesSelected,
  onToolCallUpdate,
  onError,
  onComplete,
}: UseStreamingChatParams = {}) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentCompletion, setCurrentCompletion] = useState('');
  const [toolCallSteps, setToolCallSteps] = useState<ToolCallStep[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // AbortController for canceling streaming requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track streaming response for partial responses
  const streamingResponseRef = useRef<string>('');

  // Store callbacks in refs to keep them stable
  const onMessageAddRef = useRef(onMessageAdd);
  const onMessageUpdateRef = useRef(onMessageUpdate);
  const onSessionUpdateRef = useRef(onSessionUpdate);
  const onFilesSelectedRef = useRef(onFilesSelected);
  const onToolCallUpdateRef = useRef(onToolCallUpdate);
  const onErrorRef = useRef(onError);
  const onCompleteRef = useRef(onComplete);

  // Update refs when callbacks change
  onMessageAddRef.current = onMessageAdd;
  onMessageUpdateRef.current = onMessageUpdate;
  onSessionUpdateRef.current = onSessionUpdate;
  onFilesSelectedRef.current = onFilesSelected;
  onToolCallUpdateRef.current = onToolCallUpdate;
  onErrorRef.current = onError;
  onCompleteRef.current = onComplete;

  const startStreaming = useCallback(
    async (question: string, options: StreamingChatOptions) => {
      try {
        const {
          mode,
          teamId,
          fileId,
          fileIds,
          sessionId,
          mediaAnalysisOptions,
          searchLimit,
          enabledTools,
          model,
        } = options;

        console.log(
          '[useStreamingChat] Starting with mode:',
          mode,
          'options:',
          options,
        );

        // Validate required parameters
        if (mode === 'single' && !fileId) {
          throw new Error('fileId is required for single mode');
        }
        // No validation needed for fileIds in multi mode - will auto-search if empty

        // Add user message to conversation
        const userMessage: StreamingChatMessage = {
          role: 'user',
          content: question,
        };
        onMessageAddRef.current?.(userMessage);

        // Set streaming state
        setIsStreaming(true);
        setCurrentCompletion('');

        // Clear previous streaming response
        streamingResponseRef.current = '';

        // Create AbortController for cancellation
        abortControllerRef.current = new AbortController();

        // Initialize tool call steps for multi mode without files or groq mode
        const isAutoSearch =
          mode === 'multi' && (!fileIds || fileIds.length === 0);
        const isGroqMode = mode === 'groq';

        if (isAutoSearch) {
          const searchStep: ToolCallStep = {
            id: 'search-files',
            type: 'search',
            description: 'Searching for relevant recordings...',
            status: 'running',
          };
          setIsSearching(true);
          setToolCallSteps([searchStep]);
          onToolCallUpdateRef.current?.([searchStep]);
        } else if (isGroqMode) {
          // For groq mode, we'll add tool call steps dynamically as they happen
          setToolCallSteps([]);
          setIsSearching(false);
        } else {
          setToolCallSteps([]);
          setIsSearching(false);
        }

        // Add empty assistant message that will be updated during streaming
        const assistantMessage: StreamingChatMessage = {
          role: 'assistant',
          content: '',
        };
        onMessageAddRef.current?.(assistantMessage);

        let fullResponse = '';
        let newSessionId = sessionId || '';

        // Use appropriate streaming endpoint based on mode
        if (mode === 'single') {
          const result = await askSingleFileQuestionStream(
            teamId,
            fileId!,
            question,
            sessionId,
            (accumulatedText: string) => {
              fullResponse = accumulatedText;
              streamingResponseRef.current = accumulatedText;

              // Use flushSync for immediate rendering
              flushSync(() => {
                setCurrentCompletion(accumulatedText);
                onMessageUpdateRef.current?.(accumulatedText);
              });
            },
            {
              signal: abortControllerRef.current.signal,
              mediaAnalysisOptions,
            },
          );
          newSessionId = result.sessionId;
        } else if (mode === 'groq') {
          // Track active tool calls for groq mode
          const activeToolCalls: Map<string, ToolCallStep> = new Map();

          // Helper to get human-readable tool description
          const getToolDescription = (
            toolName: string,
            args: Record<string, unknown>,
          ): string => {
            switch (toolName) {
              case 'search_content':
                return `Searching: "${args.query || 'content'}"`;
              case 'list_recent_files':
                return `Listing recent recordings${args.limit ? ` (${args.limit})` : ''}`;
              case 'get_file_summary':
                return 'Getting file summary';
              case 'get_user_memory':
                return 'Accessing your preferences';
              case 'trim_video':
                return `Trimming video${args.start_time !== undefined && args.end_time !== undefined ? ` (${args.start_time}s - ${args.end_time}s)` : ''}`;
              case 'combine_videos':
                return `Combining ${(args.file_ids as string[])?.length || ''} files`;
              default:
                return `Running ${toolName}`;
            }
          };

          const groqOptionsObj = {
            signal: abortControllerRef.current.signal,
            onFilesReceived: (receivedFileIds: string[]) => {
              onFilesSelectedRef.current?.(receivedFileIds);
            },
            onToolCallStart: (toolCall: ToolCallEvent) => {
              const step: ToolCallStep = {
                id: toolCall.toolId || `tool-${Date.now()}`,
                type: 'tool',
                toolName: toolCall.toolName,
                description: getToolDescription(
                  toolCall.toolName,
                  toolCall.args,
                ),
                status: 'running',
                args: toolCall.args,
              };
              activeToolCalls.set(step.id, step);

              const steps = Array.from(activeToolCalls.values());
              flushSync(() => {
                setToolCallSteps(steps);
                setIsSearching(true);
              });
              onToolCallUpdateRef.current?.(steps);
            },
            onToolCallComplete: (toolCall: ToolCallEvent) => {
              const stepId = toolCall.toolId || '';
              const existingStep = activeToolCalls.get(stepId);
              if (existingStep) {
                existingStep.status = 'completed';
                existingStep.result = toolCall.result;

                const steps = Array.from(activeToolCalls.values());
                flushSync(() => {
                  setToolCallSteps(steps);
                  // Check if all tool calls are completed
                  const allCompleted = steps.every(
                    (s) => s.status === 'completed',
                  );
                  if (allCompleted) {
                    setIsSearching(false);
                  }
                });
                onToolCallUpdateRef.current?.(steps);
              }
            },
          };

          const result = await askGroqWithToolsStream(
            teamId,
            {
              fileIds: fileIds || [],
              question,
              sessionId: sessionId || undefined,
              enabledTools,
              model: model || 'kimi-k2',
            },
            (accumulatedText: string) => {
              fullResponse = accumulatedText;
              streamingResponseRef.current = accumulatedText;

              // Use flushSync for immediate rendering
              flushSync(() => {
                setCurrentCompletion(accumulatedText);
                onMessageUpdateRef.current?.(accumulatedText);
              });
            },
            groqOptionsObj,
          );
          newSessionId = result.sessionId;
        } else {
          // Multi mode
          const optionsObj = {
            signal: abortControllerRef.current.signal,
            onFilesReceived: (receivedFileIds: string[]) => {
              console.log(
                '[useStreamingChat] onFilesReceived callback triggered with:',
                receivedFileIds,
              );
              // Mark search step as completed when files are received
              if (isAutoSearch) {
                const completedStep: ToolCallStep = {
                  id: 'search-files',
                  type: 'search',
                  description: `Found ${receivedFileIds.length} relevant recording${receivedFileIds.length === 1 ? '' : 's'}`,
                  status: 'completed',
                };
                setToolCallSteps([completedStep]);
                setIsSearching(false);
                onToolCallUpdateRef.current?.([completedStep]);
              }
              onFilesSelectedRef.current?.(receivedFileIds);
            },
          };
          console.log(
            '[useStreamingChat] About to call askMultiFileQuestionStream with options:',
            {
              hasSignal: !!optionsObj.signal,
              hasOnFilesReceived: !!optionsObj.onFilesReceived,
              optionsKeys: Object.keys(optionsObj),
            },
          );
          const result = await askMultiFileQuestionStream(
            teamId,
            {
              fileIds: fileIds || [],
              question,
              sessionId: sessionId || undefined,
              searchLimit,
            },
            (accumulatedText: string) => {
              fullResponse = accumulatedText;
              streamingResponseRef.current = accumulatedText;

              // Use flushSync for immediate rendering
              flushSync(() => {
                setCurrentCompletion(accumulatedText);
                onMessageUpdateRef.current?.(accumulatedText);
              });
            },
            optionsObj,
          );
          newSessionId = result.sessionId;

          console.log(
            '[useStreamingChat] Received result from askMultiFileQuestionStream:',
            result,
          );
        }

        // Clear abort controller after successful completion
        abortControllerRef.current = null;
        streamingResponseRef.current = '';

        // Sanitize the complete response
        const sanitizedCompletion = sanitizeMarkdown(fullResponse);

        // Update with final sanitized content
        flushSync(() => {
          setCurrentCompletion(sanitizedCompletion);
          onMessageUpdateRef.current?.(sanitizedCompletion);
        });

        // Update session ID
        if (newSessionId) {
          onSessionUpdateRef.current?.(newSessionId);
        }

        onCompleteRef.current?.();
      } catch (error) {
        console.error('Streaming error:', error);

        // Clear abort controller
        abortControllerRef.current = null;

        // Check if error was due to abort
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled - save partial response if available
          if (streamingResponseRef.current) {
            const sanitizedPartial = sanitizeMarkdown(
              streamingResponseRef.current,
            );
            flushSync(() => {
              setCurrentCompletion(sanitizedPartial);
              onMessageUpdateRef.current?.(sanitizedPartial);
            });
          }
          console.log('Request was cancelled by user');
        } else {
          onErrorRef.current?.(
            error instanceof Error ? error : new Error('Unknown error'),
          );
        }

        streamingResponseRef.current = '';
      } finally {
        setIsStreaming(false);
        // Clear tool call steps after a delay to allow completion animation
        setTimeout(() => {
          setToolCallSteps([]);
          setIsSearching(false);
        }, 1000);
      }
    },
    [], // Empty deps - callbacks are accessed via refs
  );

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;

      // Save partial response if available
      const partialResponse = streamingResponseRef.current;
      if (partialResponse) {
        const sanitizedCompletion = sanitizeMarkdown(partialResponse);
        flushSync(() => {
          setCurrentCompletion(sanitizedCompletion);
          onMessageUpdateRef.current?.(sanitizedCompletion);
        });
        streamingResponseRef.current = '';
      }

      setIsStreaming(false);
      setToolCallSteps([]);
      setIsSearching(false);
    }
  }, []); // Empty deps - callbacks are accessed via refs

  return {
    isStreaming,
    currentCompletion,
    toolCallSteps,
    isSearching,
    startStreaming,
    stopStreaming,
  };
};
